import { Grid, List } from "@arco-design/web-react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";

import { User } from "../../models/Person";
import { RootState } from "../../store/store";
import { useTranslations } from '../../utils/hooks/useTranslations';

import './UserDetailedInfo.css';

type Props = {
  user: User;
}

const Row = Grid.Row;
const Col = Grid.Col;

export function UserDetailedInfo({ user }: Props) {
  const { t } = useTranslation();
  const {translateSelected} = useTranslations();
  const isSurveyForm = useSelector((state: RootState) => state.config.isSurveyForm);
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);

  if (user.isAdmin) {
    const statusLabel = user.accountStatus === 'invited'
      ? t('admin.users_list.status_invited')
      : t('admin.users_list.status_active');

    return (
      <List className="user-detailed-info">
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.name_label')}</Col>
            <Col span={14}>{user.name}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.last_name_label')}</Col>
            <Col span={14}>{user.last_name || ' - '}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.email_label')}</Col>
            <Col span={14}>{user.email}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.short_description_label')}</Col>
            <Col span={14}>{user.shortDescription || '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.linkedin_label')}</Col>
            <Col span={14}>{user.linkedIn || '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.website_label')}</Col>
            <Col span={14}>{user.website || '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.users_list.role_label')}</Col>
            <Col span={14}>
              {user.isSuperAdmin ? t('admin.users_list.super_admin') : t('admin.users_list.admin')}
            </Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.users_list.status_label')}</Col>
            <Col span={14}>{statusLabel}</Col>
          </Row>
        </List.Item>
      </List>
    );
  }

  const isBusinessFieldsAvailable = user.occupationStatus === 'Entrepreneur (Business Owner/Self-Employed)';

  return (
    <List className="user-detailed-info">
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.name_label')}</Col>
          <Col span={14}>{user.name}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.last_name_label')}</Col>
          <Col span={14}>{user.last_name || ' - '}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.email_label')}</Col>
          <Col span={14}>{user.email}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.linkedin_label')}</Col>
          <Col span={14}>{user.linkedIn || '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.location_label')}</Col>
          <Col span={14}>{user.location || '-'}</Col>
        </Row>
      </List.Item>
      {!useNycIntake && (
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.borough_label')}</Col>
          <Col span={14}>{user.borough ? translateSelected('borough', user.borough) : '-'}</Col>
        </Row>
      </List.Item>
      )}
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.gender_label')}</Col>
          <Col span={14}>{user.gender ? translateSelected('gender', user.gender) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.age_label')}</Col>
          <Col span={14}>{user.age ? translateSelected('age', user.age) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.race_label')}</Col>
          <Col span={14}>{user.race ? translateSelected('race', user.race) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.ethnicity_label')}</Col>
          <Col span={14}>{user.ethnicity ? translateSelected('ethnicity', user.ethnicity) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.education_label')}</Col>
          <Col span={14}>{user.education ? translateSelected('education', user.education) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.income_range_label')}</Col>
          <Col span={14}>{user.incomeRange ? translateSelected('incomeRange', user.incomeRange) : '-'}</Col>
        </Row>
      </List.Item>
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.occupation_status_label')}</Col>
          <Col span={14}>{user.occupationStatus ? translateSelected('occupationStatus', user.occupationStatus) : '-'}</Col>
        </Row>
      </List.Item>
      {!user.isMentor && isSurveyForm && <>
        <br />
        {isBusinessFieldsAvailable && <><List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.business_start_period_label')}</Col>
            <Col span={14}>{user.business_start_period ? translateSelected('business_start_period', user.business_start_period) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.mwbe_label')}</Col>
            <Col span={14}>{user.mwbe ? translateSelected('mwbe', user.mwbe) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.employees_count_label')}</Col>
            <Col span={14}>{user.employees_count ? translateSelected('employees_count', user.employees_count) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.employees_count_label')}</Col>
            <Col span={14}>{user.employees_count ? translateSelected('employees_count', user.employees_count) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.business_stage_label')}</Col>
            <Col span={14}>{user.business_stage ? translateSelected('business_stage', user.business_stage) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.revenue_label')}</Col>
            <Col span={14}>{user.revenue ? translateSelected('revenue', user.revenue) : '-'}</Col>
          </Row>
        </List.Item>
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.items_type_label')}</Col>
            <Col span={14}>{user.item_types ? translateSelected('items_type', user.item_types) : '-'}</Col>
          </Row>
        </List.Item></>}
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.three_goals_label')}</Col>
            <Col span={14}>{user.three_goals?.length ? translateSelected('three_goals', user.three_goals).join(', ') : '-'}</Col>
          </Row>
        </List.Item>
        <br />
      </>}
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.professional_experience_label')}</Col>
          <Col span={14}>{user.professionalExperience ? translateSelected('professionalExperience', user.professionalExperience) : '-'}</Col>
        </Row>
      </List.Item>
      {!user.isMentor && useNycIntake && (
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.current_industry_label')}</Col>
            <Col span={14}>
              {(() => {
                const raw = user.current_industry;
                const value = Array.isArray(raw) ? raw[0] : raw;
                return value ? translateSelected('industries_nyc', value) : '-';
              })()}
            </Col>
          </Row>
        </List.Item>
      )}
      {user.isMentor && (
      <List.Item>
        <Row gutter={8}>
          <Col span={10}>{t('admin.user_form.industry_label')}</Col>
          <Col span={14}>{user.industries?.length ? translateSelected('industries', user.industries).join(', ') : '-'}</Col>
        </Row>
      </List.Item>
      )}
      {!user.isMentor && (
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.high_industries_label')}</Col>
            <Col span={14}>{user.highGrowthIndustries?.length ? translateSelected('highGrowthIndustry', user.highGrowthIndustries).join(', ') : '-'}</Col>
          </Row>
        </List.Item>
      )}
      {!user.isMentor && isSurveyForm ? (
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.important_goal_label')}</Col>
            <Col span={14}>{user.areaInterest?.length ? translateSelected('areaInterest', user.areaInterest).join(', ') : '-'}</Col>
          </Row>
        </List.Item>
      ) : !user.isMentor && (
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.interests_label')}</Col>
            <Col span={14}>{user.areaInterest ? translateSelected('areaInterest', user.areaInterest).join(', ') : '-'}</Col>
          </Row>
        </List.Item>
      )}
      {!user.isMentor && isSurveyForm && (
        <List.Item>
          <Row gutter={8}>
            <Col span={10}>{t('admin.user_form.source_label')}</Col>
            <Col span={14}>{user.howYouFoundUs ? translateSelected('howYouFoundUs', user.howYouFoundUs) : '-'}</Col>
          </Row>
        </List.Item>
      )}
    </List>
  )
}